import React, { useState } from "react";
import PropTypes from "prop-types";
import HelperText from "../../../../../components/atoms/HelperText";
import {
  getVarsoUrl,
  renderMainDataPoint,
} from "../../../../../common/helpers/helpers";
import { GnomadExplainText, GnomadVersion, GnomadUrl } from "./components";
import RenderExplainLink from "../../../../../components/atoms/RenderExplainLink/RenderExplainLink";
import { flagsExplanation } from "./constants/flagsExplanation";
import PopulationFrequenciesTable from "./components/PopulationFrequenciesTable/PopulationFrequenciesTable";
import { GNOMAD_TYPE_MAPPINGS } from "./constants";

const Gnomad = ({ variantResult, genome, gnomadType, isMito }) => {
  if (!variantResult) return null;
  const [explainVisible, setExplainVisible] = useState(false);
  const setVisible = (visible) => {
    setExplainVisible(visible);
  };

  let tmpl = null;

  const mapping = gnomadType ? GNOMAD_TYPE_MAPPINGS[gnomadType] : null;
  const values = variantResult[mapping?.db];
  const valuesCoverage = variantResult[mapping?.dbCoverage];
  const title = mapping?.title;
  const helpText = mapping?.helpText;

  if (values || valuesCoverage) {
    const valuesLen = (Array.isArray(values) && values.length) || 0;
    const valuesCoverageLen =
      (Array.isArray(valuesCoverage) && valuesCoverage.length) || 0;

    const exacs = [];
    let i = 0;

    while (i < valuesLen || i < valuesCoverageLen) {
      const item = valuesLen > i ? values[i] : null;
      const itemCoverage = valuesCoverageLen > i ? valuesCoverage[i] : null;
      const filterFailed =
        item?.filter &&
        (item.filter.includes("RF") || item.filter.includes("InbreedingCoeff"));

      exacs.push(
        <div key={i}>
          <div className="varsome-title varsome-clearfix">
            <a
              href={getVarsoUrl("http://gnomad.broadinstitute.org/")}
              target="_blank"
            >
              {title}
            </a>
            <HelperText fieldDescriptionKey={helpText} />
            {item && <GnomadVersion version={item?.version} />}
            {item && renderMainDataPoint(item?.main_data)}
            {item && item?.ac && item?.an && (
              <RenderExplainLink
                setExplainVisible={setVisible}
                label="Explain"
              />
            )}
            {item && (
              <GnomadUrl
                variantResult={variantResult}
                gnomadType={gnomadType}
                refGenome={genome?.name}
                version={item.version}
              />
            )}
          </div>
          <div>
            {item && (
              <GnomadExplainText
                chromosome={variantResult.chromosome}
                explainVisible={explainVisible}
                item={item}
                isMito={isMito}
              />
            )}
            <div className="varsome-inner-container">
              {item && (
                <div>
                  {item.filter && filterFailed && (
                    <div className="varsome-text-center">
                      <strong className="tw-text-red-600">
                        {`GnomAD have indicated that the data quality is suspect with code ${item.filter.replace(
                          ";",
                          " & "
                        )} `}
                      </strong>
                      <HelperText RenderContent={() => flagsExplanation} />
                    </div>
                  )}
                  {item.lcr && (
                    <div className="varsome-text">
                      Variant falls within a low complexity region.
                    </div>
                  )}
                  {item.segdup && (
                    <div className="varsome-text">
                      Variant falls within a segmental duplication region.
                    </div>
                  )}
                  {item.variant_type && (
                    <div className="varsome-text">
                      <b>{"Variant type: "}</b>
                      {item.variant_type}
                    </div>
                  )}
                  <h5>
                    Population frequencies
                    <HelperText fieldDescriptionKey="Population frequencies" />
                  </h5>
                  <PopulationFrequenciesTable
                    item={item}
                    isMito={isMito}
                    gnomadType={gnomadType}
                    filterFailed={filterFailed}
                  />
                </div>
              )}
              {!item && itemCoverage && (
                <div className="tw-text-center">
                  <small>
                    {`This variant does not have a gnomAD ${gnomadType} entry, but its locus is covered in gnomAD ${gnomadType} as follows.`}
                  </small>
                </div>
              )}
            </div>
          </div>
        </div>
      );
      i += 1;
    }
    tmpl = exacs.slice();
  }

  return tmpl;
};

Gnomad.propTypes = {
  variantResult: PropTypes.shape({
    gnomad_exomes_coverage: PropTypes.arrayOf(
      PropTypes.shape({
        version: PropTypes.string,
        coverage_mean: PropTypes.arrayOf(PropTypes.number),
        coverage_median: PropTypes.arrayOf(PropTypes.number),
        coverage_20_frequency: PropTypes.arrayOf(PropTypes.number),
      })
    ),
    gnomad_genomes_coverage: PropTypes.arrayOf(
      PropTypes.shape({
        version: PropTypes.string,
        coverage_mean: PropTypes.arrayOf(PropTypes.number),
        coverage_median: PropTypes.arrayOf(PropTypes.number),
        coverage_20_frequency: PropTypes.arrayOf(PropTypes.number),
      })
    ),
    gnomad_exomes: PropTypes.arrayOf(
      PropTypes.shape({
        title: PropTypes.string,
      })
    ),
    gnomad_genomes: PropTypes.arrayOf(
      PropTypes.shape({
        title: PropTypes.string,
      })
    ),
    gnomad_mito: PropTypes.arrayOf(
      PropTypes.shape({
        title: PropTypes.string,
      })
    ),
    chromosome: PropTypes.string,
  }),
  genome: PropTypes.shape({
    name: PropTypes.string,
  }),
  isMito: PropTypes.bool,
  gnomadType: PropTypes.string,
};

Gnomad.defaultProps = {
  variantResult: {},
  genome: {},
  isMito: false,
  gnomadType: null,
};

export default Gnomad;
